import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "./Blog.css";
import { getBlogPostBySlug } from "./PagesData/BlogPosts";
import { fetchBlogPostBySlug } from "../api/blogApi";
import { isBlogAdmin } from "../utils/authClient";

function imageFileStem(src) {
  const name = String(src || "").split("/").pop() || "";
  return name.replace(/-inline(\.[^.]+)?$/i, "").replace(/\.[^.]+$/, "");
}

function isDuplicateCoverImage(coverSrc, inlineSrc, slug) {
  if (!coverSrc || !inlineSrc) return false;
  if (String(coverSrc) === String(inlineSrc)) return true;

  const coverStem = imageFileStem(coverSrc);
  const inlineStem = imageFileStem(inlineSrc);
  if (coverStem && inlineStem && coverStem === inlineStem) return true;

  if (slug) {
    const slugStem = String(slug).replace(/\.[^.]+$/, "");
    if (
      inlineStem.includes(slugStem) &&
      (coverStem.includes(slugStem) || String(coverSrc).includes(slugStem))
    ) {
      return true;
    }
  }

  return false;
}

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(() => getBlogPostBySlug(slug));

  useEffect(() => {
    setPost(getBlogPostBySlug(slug));
    let cancelled = false;
    (async () => {
      const remote = await fetchBlogPostBySlug(slug);
      if (!cancelled && remote) {
        setPost(remote);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!post) {
    return (
      <div className="blog_page">
        <div className="blog_container">
          <div className="blog_post">
            <h1 className="blog_post_title">Artículo no encontrado</h1>
            <p className="blog_post_p">
              El artículo que buscas no existe o fue movido.
            </p>
            <Link className="blog_back" to="/blog">
              Volver al blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog_page">
      <div className={`blog_hero blog_hero_post blog_card_theme_${post.coverTheme}`}>
        <div className="blog_hero_inner">
          <p className="blog_kicker">
            <Link className="blog_breadcrumb" to="/blog">
              Blog
            </Link>{" "}
            <span className="blog_dot">•</span> {post.date}{" "}
            <span className="blog_dot">•</span> {post.readTime}
          </p>
          <h1 className="blog_title">{post.title}</h1>
          <p className="blog_subtitle">{post.excerpt}</p>
        </div>
      </div>

      <div className="blog_container">
        <div className="blog_post">
          {post.coverImage ? (
            <div className="blog_post_featured">
              <img
                src={post.coverImage}
                alt={post.title}
                className="blog_post_featured_img"
                width={942}
                height={721}
                fetchPriority="high"
                decoding="async"
              />
            </div>
          ) : null}
          <div className="blog_post_internal_links">
            {(Array.isArray(post.relatedLinks) && post.relatedLinks.length
              ? post.relatedLinks
              : [
                  {
                    to: "/blog/orthodontics-cusco-braces-aligners",
                    label: "Braces & aligners in Cusco",
                  },
                  { to: "/emergency-dentist-cusco", label: "Emergency dentist in Cusco" },
                ]
            ).map((link) => (
              <Link key={link.to} className="blog_post_linkchip" to={link.to}>
                {link.label}
              </Link>
            ))}
          </div>
          {(Array.isArray(post.content) ? post.content : []).map((block, idx) => {
            if (!block || typeof block !== "object") return null;
            if (block.type === "h3") {
              return (
                <h3 key={idx} className="blog_post_h3">
                  {block.text}
                </h3>
              );
            }
            if (block.type === "ul" && Array.isArray(block.items)) {
              return (
                <ul key={idx} className="blog_post_ul">
                  {block.items.map((it, j) => (
                    <li key={j}>{it}</li>
                  ))}
                </ul>
              );
            }
            if (block.type === "note") {
              return (
                <div key={idx} className="blog_note">
                  {block.text}
                </div>
              );
            }
            if (block.type === "cta" && block.to) {
              return (
                <p key={idx} className="blog_post_p">
                  <Link className="blog_post_linkchip" to={block.to}>
                    {block.text || block.to}
                  </Link>
                </p>
              );
            }
            if (block.type === "img" && block.src) {
              if (isDuplicateCoverImage(post.coverImage, block.src, post.slug)) {
                return null;
              }
              return (
                <figure key={idx} className="blog_post_figure">
                  <img
                    src={block.src}
                    alt={block.alt || ""}
                    className="blog_post_inline_img"
                    loading="lazy"
                  />
                  {block.alt ? (
                    <figcaption className="blog_post_caption">{block.alt}</figcaption>
                  ) : null}
                </figure>
              );
            }
            return (
              <p key={idx} className="blog_post_p">
                {block.text}
              </p>
            );
          })}

          {isBlogAdmin() ? (
            <div className="blog_post_admin_bar">
              <Link
                className="blog_edit_post"
                to={`/admin/blog/edit/${encodeURIComponent(post.slug)}`}
              >
                Editar post
              </Link>
            </div>
          ) : null}

          <div className="blog_post_footer">
            <Link className="blog_back" to="/blog">
              Volver al blog
            </Link>
            <Link className="blog_cta" to="/reservas">
              Agendar una cita
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
